<template>
  <form action="">
    <div class="modal-card round-details">
      <section class="modal-card-body p-0">
        <div class="layout-container">
          <div class="pairing-stats-section">
            <TournamentPlayerTileContainer
              :tournament="tournament"
              :players="players"
              :games="games"
              :round="round"
              :roundGames="localRoundGames"
              :isDraggable="false"
              @set-players="setGamePlayers"
            />
          </div>

          <!-- Visual divider -->
          <div class="divider-vertical"></div>

          <div class="table-section">
            <TournamentRoundTable
              :tournament="tournament"
              :players="players"
              :round="round"
              :roundGames="localRoundGames"
              :savedRoundGames="roundGames"
              :allGames="games"
              @onRoundGamesUpdated="handleRoundGamesUpdated"
              @onStartGame="startGame"
              @onRematchGame="rematchGame"
            />

            <!-- Round actions below the table -->
            <div class="round-actions-footer">
              <!-- Loading indicator -->
              <div
                v-if="isProcessing"
                class="notification is-info is-light mb-3"
              >
                <b-loading
                  :is-full-page="false"
                  :active="isProcessing"
                ></b-loading>
                Processing round operation...
              </div>

              <div class="buttons">
                <!-- Abort Button -->
                <b-button
                  v-if="abortBtn"
                  :disabled="hasUnsavedChanges || isProcessing"
                  label="Abort"
                  type="is-danger"
                  @click="showAbortConfirmation"
                />

                <!-- Reset Button -->
                <b-button
                  :disabled="hasUnsavedChanges || isProcessing"
                  label="Reset"
                  type="is-dark"
                  @click="showResetConfirmation"
                />

                <!-- Pair Button -->
                <b-button
                  :label="`Pair (${tournament.type})`"
                  type="is-primary"
                  :loading="isProcessing"
                  @click="showPairConfirmation"
                />

                <!-- Revert Button -->
                <b-button
                  v-if="hasUnsavedChanges"
                  label="Revert"
                  type="is-info"
                  :disabled="isProcessing"
                  @click="handleRevertRound"
                />

                <!-- Save Button -->
                <b-button
                  label="Save"
                  type="is-info"
                  :loading="isProcessing"
                  @click="handleSaveRound"
                />

                <!-- Start Games Button (migrated from TournamentRound.vue) -->
                <b-button
                  v-if="startGamesBtn"
                  label="Start Games"
                  type="is-success"
                  :disabled="!canStartRound || isProcessing"
                  @click="handleStartGames"
                />

                <!-- Finish Round Button (migrated from TournamentRound.vue) -->
                <b-button
                  v-if="
                    !round.finishTime &&
                    tournament.currentRound === round.roundNumber
                  "
                  label="Finish Round"
                  type="is-danger"
                  :disabled="!canFinishRound || isProcessing"
                  @click="handleFinishRound"
                />

                <!-- Unfinish Round Button (migrated from TournamentRound.vue) -->
                <b-button
                  v-if="
                    round.finishTime &&
                    tournament.currentRound === round.roundNumber
                  "
                  label="Unfinish Round"
                  type="is-danger"
                  :disabled="hasUnsavedChanges || isProcessing"
                  @click="handleUnfinishRound"
                />

                <!-- Simulation Button -->
                <b-button
                  v-if="simulationBtn"
                  :disabled="hasUnsavedChanges || isProcessing"
                  label="Simulation"
                  type="is-default"
                  @click="handleSimulateRound"
                />

                <!-- Copy Button -->
                <b-button
                  v-if="simulationBtn"
                  :disabled="hasUnsavedChanges || isProcessing"
                  label="Copy"
                  type="is-default"
                  @click="handleCopyRound"
                />

                <!-- Clear Results Button -->
                <b-button
                  :disabled="hasUnsavedChanges || isProcessing"
                  type="is-default"
                  label="Clear results"
                  @click="handleClearResults"
                />

                <!-- Present Button -->
                <button-tooltip
                  tooltip="Open presentation mode in new window (F11)"
                  label="Present"
                  type="is-info"
                  icon-left="desktop"
                  @click="handlePresentRound"
                />

                <!-- Unsaved changes notification -->
                <div
                  v-if="hasUnsavedChanges"
                  class="notification is-warning is-light mb-2"
                >
                  ⚠️ You have unsaved changes. Save or revert before proceeding.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </form>
</template>

<script>
import TournamentPlayerTileContainer from "@/components/admins/tournaments/TournamentPlayerTileContainer.vue";
import TournamentRoundTable from "@/components/admins/tournaments/TournamentRoundTable.vue";
import TournamentRoundGames from "@/components/admins/tournaments/TournamentRoundGames.js";
import TournamentRoundManager from "@/components/admins/tournaments/TournamentRoundManager.js";
import { RoundStatus } from "vue-chess-shared/tournament/services/interfaces/ITournamentRoundService";
import { TOURNAMENT_GAME_RESULT } from "vue-chess-shared/constants";
import { hasUnsavedChanges } from "vue-chess-shared/tournament/utils/gameUtils";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";

export default {
  name: "TournamentRoundDetails",

  components: {
    TournamentPlayerTileContainer,
    TournamentRoundTable,
    ButtonTooltip,
  },

  props: [
    "tournament",
    "rounds",
    "games",
    "players",
    "round",
    "elapsed",
    "abortBtn",
    "startGamesBtn",
    "fixResultsBtn",
    "simulationBtn",
  ],

  mixins: [TournamentRoundGames, TournamentRoundManager],

  data() {
    return {
      isProcessing: false,
      localRoundGames: [], // Editable local state that can differ from database
    };
  },

  computed: {
    isOfflineClassTournament() {
      // Check if this is an offline class tournament by looking at the route
      // Class tournaments are accessed via /teacher/class-tournaments/:id
      return this.$route.name === "OfflineClassTournament";
    },

    /**
     * Check if local edits differ from database state using shared utility
     */
    hasUnsavedChanges() {
      if (!this.localRoundGames || !this.roundGames) {
        return false;
      }

      // Check if any game in the round has unsaved changes
      return this.localRoundGames.some((localGame, index) => {
        const savedGame = this.roundGames[index];
        return savedGame && hasUnsavedChanges(localGame, savedGame);
      });
    },

    /**
     * Get current round status based on round state and games
     */
    currentRoundStatus() {
      if (!this.round || !this.localRoundGames) {
        return RoundStatus.CREATED;
      }

      // Check if round has been started
      if (
        this.round.startTime &&
        this.tournament.currentRound >= this.round.roundNumber
      ) {
        if (this.round.finishTime) {
          return RoundStatus.FINISHED;
        }
        return RoundStatus.STARTED;
      }

      // Check if round has pairings
      const hasPairings = this.localRoundGames.some(
        (game) => game.whiteUsername && game.blackUsername
      );
      if (hasPairings) {
        return RoundStatus.PAIRED;
      }

      return RoundStatus.CREATED;
    },

    /**
     * Check if all games in the round have results
     */
    allGamesHaveResults() {
      if (!this.localRoundGames || this.localRoundGames.length === 0) {
        return false;
      }

      const pairedGames = this.localRoundGames.filter(
        (game) => game.whiteUsername && game.blackUsername
      );
      if (pairedGames.length === 0) {
        return false;
      }

      return pairedGames.every(
        (game) =>
          game.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED &&
          game.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED
      );
    },

    /**
     * Check if round can be started (paired and saved)
     */
    canStartRound() {
      return (
        this.currentRoundStatus === RoundStatus.PAIRED &&
        !this.hasUnsavedChanges &&
        this.round.roundNumber === this.tournament.currentRound + 1 &&
        (this.round.roundNumber === 1 || this.previousRound?.finishTime)
      );
    },

    /**
     * Check if round can be finished (started and all games have results)
     */
    canFinishRound() {
      return (
        this.currentRoundStatus === RoundStatus.STARTED &&
        this.tournament.currentRound === this.round.roundNumber &&
        this.allGamesHaveResults
      );
    },

    /**
     * Get previous round for validation
     */
    previousRound() {
      if (!this.round || !this.rounds || this.round.roundNumber <= 1) {
        return null;
      }
      return this.rounds[this.round.roundNumber - 2];
    },
  },

  watch: {
    // Only sync localRoundGames on initial load when it's empty
    roundGames: {
      immediate: true,
      handler(newRoundGames) {
        if (!newRoundGames || newRoundGames.length === 0) {
          return;
        }

        if (!this.localRoundGames || this.localRoundGames.length === 0) {
          this.syncLocalWithDatabase();
        }

        if (this.hasUnsavedChanges) {
          this.syncLocalWithDatabase();
        }
      },
    },
  },

  methods: {
    /**
     * Explicitly sync local state with database state (roundGames from mixin)
     * Called on revert to discard local changes
     */
    syncLocalWithDatabase() {
      console.log(
        `syncLocalWithDatabase round ${this.round.roundNumber}: ${
          this.roundGames.filter((g) => g.whiteUsername && g.blackUsername)
            .length
        } paired games`
      );
      this.localRoundGames = JSON.parse(JSON.stringify(this.roundGames || []));
    },

    startGame(game) {
      this.handleStartGame(game);
    },

    rematchGame(game) {
      this.handleRematchGame(game);
    },

    handleRoundGamesUpdated(newRoundGames) {
      this.localRoundGames = [...newRoundGames];
    },

    /**
     * Override handleClearResults to operate on localRoundGames instead of roundGames
     */
    handleClearResults() {
      this.localRoundGames.forEach((g) => {
        if (g.whiteUsername && g.blackUsername) {
          g.whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
          g.blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
        }
      });
      this.$buefy.toast.open({
        message: `Round ${this.round.roundNumber} results cleared. Save to persist changes.`,
        type: "is-warning",
      });
    },

    /**
     * Handle Present Round - opens a presentation view in a new window using Vue Router
     */
    handlePresentRound() {
      // Open in classroom-friendly resolution window
      const presentationWindow = window.open(
        `${window.location.href}/presentation`,
        "tournamentRoundRepresentation",
        "width=1024,height=768,scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no"
      );

      if (presentationWindow) {
        presentationWindow.focus();
      } else {
        this.$buefy.toast.open({
          message:
            "Failed to open presentation window. Please check popup blocker settings.",
          type: "is-danger",
        });
      }
    },

    /**
     * Handle auto-start event from TournamentRoundTable
     */
    handleAutoStart(round) {
      this.$emit("onStart", round);
      console.log(
        `auto-start round ${this.round.roundNumber} as all players are paired`
      );
      this.$buefy.toast.open({
        message: `Round ${round.roundNumber} started automatically - all players paired!`,
        type: "is-success",
      });
    },

    /**
     * Override showPairConfirmation to use enhanced pairing with loading state
     */
    showPairConfirmation() {
      if (
        !this.localRoundGames ||
        this.localRoundGames.find((g) => g.matchRef)
      ) {
        this.$buefy.toast.open({
          message: `There are round game(s) which have started. You have to RESET the round before re-pairing`,
          type: "is-danger",
        });
        return;
      }

      // If no existing pairings, proceed directly
      if (
        !this.localRoundGames.some((g) => g.whiteUsername || g.blackUsername)
      ) {
        this.handlePairRound();
        return;
      }

      // Show confirmation only when there are existing pairings to override
      this.$buefy.dialog.confirm({
        title: `${this.tournament.type} pair Round ${this.round.roundNumber}`,
        message: `⚠️ This action will override any existing pairings for this round. Are you sure you want to continue?`,
        cancelText: "Cancel",
        confirmText: "Pair",
        type: "is-warning",
        trapFocus: true,
        onConfirm: async () => {
          this.handlePairRound();
        },
      });
    },

    /**
     * Enhanced pair round with loading state
     */
    async handlePairRound() {
      this.isProcessing = true;
      try {
        // Emit the pairing event with a callback mechanism
        await new Promise((resolve, reject) => {
          // Create a callback function that the parent can call when done
          const pairCallback = (error, success) => {
            if (error) {
              reject(error);
            } else {
              resolve(success);
            }
          };

          // Emit the event with the callback
          this.$emit("onPair", this.round, pairCallback);
        });

        this.$buefy.toast.open({
          message: `Round ${this.round.roundNumber} paired successfully!`,
          type: "is-success",
        });
      } catch (error) {
        if (error.message && error.message.includes("No possible pairings")) {
          this.$buefy.dialog.alert({
            title: "⚠️ No Possible Pairings",
            message: `
              <div class="content">
                <p><strong>No valid pairings could be generated for Round ${this.round.roundNumber}.</strong></p>
                <p>This could happen due to:</p>
                <ul>
                  <li>Insufficient active players</li>
                  <li>All possible opponents have already played each other</li>
                  <li>Tournament constraints cannot be satisfied</li>
                </ul>
                <p>Consider:</p>
                <ul>
                  <li>Adding more players to the tournament</li>
                  <li>Ending the tournament after the previous round</li>
                  <li>Manually creating pairings if needed</li>
                </ul>
              </div>
            `,
            type: "is-danger",
            hasIcon: true,
            icon: "alert-circle",
            iconPack: "mdi",
          });
        } else {
          this.$buefy.toast.open({
            message: `Failed to pair round: ${error.message || error}`,
            type: "is-danger",
          });
        }
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Override showResetConfirmation to handle reset properly
     */
    showResetConfirmation() {
      if (
        !this.localRoundGames ||
        this.localRoundGames.find(
          (g) => g.matchRef && g.matchRef.status !== "aborted"
        )
      ) {
        this.$buefy.toast.open({
          message: `There are still un-aborted games! ABORT all games in the round first`,
          type: "is-danger",
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: `Reset Round ${this.round.roundNumber}?`,
        message: `This action would unlink all existing round games from the tournament. All of them should have been aborted`,
        cancelText: "Cancel",
        confirmText: "Reset",
        type: "is-dark",
        trapFocus: true,
        onConfirm: () => {
          this.handleResetRound();
        },
      });
    },

    /**
     * Reset round to empty games (local change only)
     */
    handleResetRound() {
      const {
        initGames,
      } = require("vue-chess-shared/tournament/utils/gameUtils");

      // Set localRoundGames to empty games - this creates unsaved changes
      // Do NOT sync with database - keep local edits separate
      this.localRoundGames = initGames(
        this.round.roundNumber,
        Math.ceil(this.players.length / 2),
        false
      );

      this.$buefy.toast.open({
        message: `Round ${this.round.roundNumber} games were reset. Save to persist the empty state.`,
        type: "is-warning",
      });
      this.$emit("onReset", this.round);
    },

    /**
     * Revert local changes back to database state
     */
    handleRevertRound() {
      this.$buefy.dialog.confirm({
        title: "Revert Changes",
        message:
          "Are you sure you want to revert all unsaved changes? This action cannot be undone.",
        cancelText: "Cancel",
        confirmText: "Revert",
        type: "is-warning",
        onConfirm: () => {
          // Explicitly sync local state with database state
          this.syncLocalWithDatabase();
          this.$buefy.toast.open({
            message: `Round ${this.round.roundNumber} reverted to last saved state.`,
            type: "is-info",
          });
        },
      });
    },

    /**
     * Save local changes to database
     */
    async handleSaveRound() {
      this.isProcessing = true;
      try {
        // Emit save event and wait for completion
        await new Promise((resolve, reject) => {
          const saveCallback = (error, success) => {
            if (error) {
              reject(error);
            } else {
              resolve(success);
              // if all participating players are paired, auto start the round
              if (
                this.isOfflineClassTournament &&
                !this.hasUnsavedChanges &&
                this.currentRoundStatus === RoundStatus.PAIRED &&
                this.round.roundNumber === this.tournament.currentRound + 1
              ) {
                // if no more player to pair, auto start the round
                const unpairedParticipatingPlayers = this.players.filter(
                  (p) =>
                    p.isParticipating &&
                    !this.localRoundGames.find(
                      (g) =>
                        g.whiteUsername === p.username ||
                        g.blackUsername === p.username
                    )
                );
                if (unpairedParticipatingPlayers.length === 0) {
                  this.handleAutoStart(this.round);
                }
              }
            }
          };

          // Emit the event with callback for proper async handling
          this.$emit("onSave", this.round, this.localRoundGames, saveCallback);
        });
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Enhanced start games with confirmation (migrated from TournamentRound.vue)
     */
    handleStartGames() {
      this.$buefy.dialog.confirm({
        title: `Start Round ${this.round.roundNumber}`,
        message:
          "Are you sure you want to start all games in this round? This action will begin the round timer.",
        cancelText: "Cancel",
        confirmText: "Start Games",
        type: "is-success",
        onConfirm: async () => {
          this.isProcessing = true;
          try {
            this.$emit("onStart", this.round);
            this.$buefy.toast.open({
              message: `Round ${this.round.roundNumber} started successfully!`,
              type: "is-success",
            });
          } catch (error) {
            this.$buefy.toast.open({
              message: `Failed to start round: ${error.message}`,
              type: "is-danger",
            });
          } finally {
            this.isProcessing = false;
          }
        },
      });
    },

    /**
     * Enhanced finish round with confirmation and auto-save (migrated from TournamentRound.vue)
     */
    handleFinishRound() {
      // Check if there are unsaved changes and inform user
      const hasUnsaved = this.hasUnsavedChanges;
      const message = hasUnsaved
        ? "Are you sure you want to finish this round? Unsaved changes will be automatically saved first, then the round will be finished and final standings calculated."
        : "Are you sure you want to finish this round? This will calculate final standings and advance to the next round.";

      this.$buefy.dialog.confirm({
        title: `Finish Round ${this.round.roundNumber}`,
        message: message,
        cancelText: "Cancel",
        confirmText: "Finish Round",
        type: "is-danger",
        onConfirm: async () => {
          this.isProcessing = true;
          try {
            // Auto-save first if there are unsaved changes
            if (hasUnsaved) {
              await this.handleSaveRound();
            }

            // Then finish the round
            this.$emit("onFinish", this.round);
          } catch (error) {
            this.$buefy.toast.open({
              message: `Failed to finish round: ${error.message}`,
              type: "is-danger",
            });
          } finally {
            this.isProcessing = false;
          }
        },
      });
    },

    /**
     * Enhanced unfinish round with confirmation (migrated from TournamentRound.vue)
     */
    handleUnfinishRound() {
      this.$buefy.dialog.confirm({
        title: `Unfinish Round ${this.round.roundNumber}`,
        message:
          "Are you sure you want to unfinish this round? This will remove the finish time and standings.",
        cancelText: "Cancel",
        confirmText: "Unfinish Round",
        type: "is-warning",
        onConfirm: async () => {
          this.isProcessing = true;
          try {
            this.$emit("onUnfinish", this.round);
          } catch (error) {
            this.$buefy.toast.open({
              message: `Failed to unfinish round: ${error.message}`,
              type: "is-danger",
            });
          } finally {
            this.isProcessing = false;
          }
        },
      });
    },

    /**
     * Handle setting players for games from drag-and-drop operations
     */
    setGamePlayers(game, whiteUsername, blackUsername) {
      // Find the game in localRoundGames and update it
      const gameIndex = this.localRoundGames.findIndex(
        (g) => g.gameId === game.gameId
      );
      if (gameIndex !== -1) {
        this.$set(this.localRoundGames, gameIndex, {
          ...this.localRoundGames[gameIndex],
          whiteUsername: whiteUsername,
          blackUsername: blackUsername,
        });
      }
    },
  },
};
</script>

<style scoped>
.round-details {
  border: 1px solid #b7b7b7;
  width: 100%;
}

.layout-container {
  display: flex;
  width: 100%;
}

.pairing-stats-section {
  max-height: 700px;
}

.divider-vertical {
  width: 2px;
  background-color: #dbdbdb;
  flex-shrink: 0;
}

.table-section {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.notification {
  padding: 0.5rem 0.5rem;
}

.round-actions-footer {
  border-top: 1px solid #dbdbdb;
  padding: 0.5rem;
}

/* Ensure buttons wrap properly */
.buttons {
  flex-wrap: wrap;
}
</style>
